/**
 * Eligibility-first gating for employee insights, explanations, and recommendations.
 * Every surfaced insight must pass applicability, evidence, confidence, and usefulness checks.
 */

const registry = require('../risk-engine/config/riskFactorRegistry');

/** Scores at or below this are treated as low retention risk for narrative gating. */
const LOW_RISK_SCORE_MAX = 33;
/** Scores at or above this may show risk drivers and actionable recommendations. */
const MEANINGFUL_RISK_SCORE_MIN = registry.levelBreakpoints.lowMax + 1;

const MIN_PRIMARY_CONTRIBUTION_PCT = 8;
const MIN_DRIVER_DETAIL_LENGTH = 18;
const COMP_GAP_RISK_PCT = 5;
const COMP_GAP_ACTION_PCT = 20;

const POSITIVE_STATUS = {
  NO_RETENTION_CONCERNS: 'No significant retention concerns identified at this time.',
  COMPENSATION_COMPETITIVE: 'Compensation appears competitive compared with market benchmarks.',
  MARKET_FAVORABLE: 'Market positioning appears favorable.',
  STABLE_OUTLOOK: 'Available indicators suggest a stable retention outlook.',
};

const GENERIC_DRIVER_RE =
  /no verified main driver|not enough data|insufficient data|review individual factors/i;

function buildRiskContext(turnover, options = {}) {
  const score = Number(turnover?.score);
  const level = String(turnover?.level || '').toLowerCase();
  const eligibilityStatus =
    turnover?.eligibility?.status != null
      ? String(turnover.eligibility.status)
      : turnover?.metadata?.eligibilityStatus != null
        ? String(turnover.metadata.eligibilityStatus)
        : null;

  const scoreReady =
    turnover?.score != null &&
    Number.isFinite(score) &&
    eligibilityStatus !== 'not_eligible' &&
    turnover?.processingState !== 'error';

  return {
    turnover,
    employee: options.employee || null,
    compBench: options.compBench || null,
    score,
    level,
    scoreReady,
    confidenceLevel: turnover?.confidenceLevel || turnover?.metadata?.confidenceLevel || null,
    eligibilityStatus,
    riskExplanation: turnover?.riskExplanation || null,
  };
}

function hasMeaningfulRetentionRisk(ctx) {
  if (!ctx?.scoreReady) return false;
  if (!Number.isFinite(ctx.score)) return false;
  if (['moderate', 'high', 'critical'].includes(ctx.level)) return true;
  return ctx.score >= MEANINGFUL_RISK_SCORE_MIN;
}

function isLowRetentionRisk(ctx) {
  if (!ctx?.scoreReady) return false;
  if (!Number.isFinite(ctx.score)) return false;
  if (ctx.score <= LOW_RISK_SCORE_MAX) return true;
  return ctx.level === 'low';
}

function hasSufficientConfidence(ctx) {
  if (ctx.eligibilityStatus === 'not_eligible') return false;
  const lvl = String(ctx.confidenceLevel || '').toLowerCase();
  if (lvl === 'low') return false;
  return true;
}

function driverHasEvidence(driver) {
  if (!driver || typeof driver !== 'object') return false;
  const detail = driver.detail != null ? String(driver.detail).trim() : '';
  if (detail.length < MIN_DRIVER_DETAIL_LENGTH) return false;
  if (GENERIC_DRIVER_RE.test(detail)) return false;
  return true;
}

function compGapPctFromContext(ctx) {
  const bench = ctx.compBench;
  if (bench?.canonicalMarket?.totalCompGapPct != null) {
    const g = Number(bench.canonicalMarket.totalCompGapPct);
    if (Number.isFinite(g)) return g;
  }
  const emp = ctx.employee;
  const salary = Number(emp?.salary);
  const market = Number(emp?.marketSalary);
  if (Number.isFinite(salary) && Number.isFinite(market) && market > 0) {
    return ((market - salary) / market) * 100;
  }
  return null;
}

/** Whether the employee profile may show any primary risk-driver content. */
function qualifiesForRiskDriverSection(ctx) {
  if (!hasMeaningfulRetentionRisk(ctx)) {
    return { eligible: false, reason: 'low_risk' };
  }
  if (!hasSufficientConfidence(ctx)) {
    return { eligible: false, reason: 'low_confidence' };
  }
  return { eligible: true };
}

function qualifiesForPrimaryRiskDriver(ctx) {
  const section = qualifiesForRiskDriverSection(ctx);
  if (!section.eligible) return section;

  const primary = ctx.riskExplanation?.primaryDriver;
  if (!driverHasEvidence(primary)) {
    return { eligible: false, reason: 'no_evidence' };
  }
  const contrib =
    primary.contributionPct != null ? Number(primary.contributionPct) : null;
  if (contrib != null && contrib < MIN_PRIMARY_CONTRIBUTION_PCT) {
    return { eligible: false, reason: 'weak_contribution' };
  }
  return { eligible: true };
}

function qualifiesForSecondaryRiskDrivers(ctx) {
  if (!qualifiesForRiskDriverSection(ctx).eligible) {
    return { eligible: false, reason: 'no_primary_context', drivers: [] };
  }
  const secondaries = Array.isArray(ctx.riskExplanation?.secondaryDrivers)
    ? ctx.riskExplanation.secondaryDrivers
    : [];
  const drivers = secondaries.filter(driverHasEvidence);
  return {
    eligible: drivers.length > 0,
    reason: drivers.length > 0 ? null : 'no_evidence',
    drivers,
  };
}

function qualifiesForProtectiveFactors(ctx, factors = []) {
  const valid = (factors || []).filter((f) => {
    const text = typeof f === 'string' ? f : f?.text;
    return String(text || '').trim().length >= 12;
  });
  if (valid.length > 0) {
    return { eligible: true, reason: null };
  }
  if (isLowRetentionRisk(ctx)) {
    return { eligible: true, reason: 'low_risk_default' };
  }
  const positives = Array.isArray(ctx.riskExplanation?.positiveSignals)
    ? ctx.riskExplanation.positiveSignals
    : [];
  if (positives.some(driverHasEvidence)) {
    return { eligible: true, reason: null };
  }
  return { eligible: false, reason: 'no_evidence' };
}

function qualifiesForCompensationInsight(ctx) {
  const gap = compGapPctFromContext(ctx);
  if (gap == null) {
    return { eligible: false, reason: 'no_market_data', message: null, tone: null };
  }
  if (gap > COMP_GAP_RISK_PCT) {
    return {
      eligible: true,
      tone: 'risk',
      message: gap > COMP_GAP_ACTION_PCT
        ? 'Pay is well below market benchmarks.'
        : 'Pay is below market benchmarks.',
    };
  }
  if (gap < -COMP_GAP_RISK_PCT) {
    return {
      eligible: true,
      tone: 'positive',
      message: POSITIVE_STATUS.COMPENSATION_COMPETITIVE,
    };
  }
  return {
    eligible: true,
    tone: 'neutral',
    message: 'Compensation is close to market benchmarks.',
  };
}

function isPlaybookRecommendationAction(action) {
  if (!action || action.isPlaceholder) return false;
  const title = String(action?.action || '').trim();
  if (!title || /^not enough data/i.test(title)) return false;
  if (action.recommendationId != null && String(action.recommendationId).trim()) return true;
  const tied = Array.isArray(action.tiedSignals) ? action.tiedSignals.filter(Boolean) : [];
  return tied.length > 0;
}

function qualifiesForRecommendations(ctx, actions = []) {
  const valid = (actions || []).filter((a) => {
    const title = String(a?.action || '').trim();
    if (!title) return false;
    if (a.isPlaceholder) return false;
    if (/^not enough data/i.test(title)) return false;
    return true;
  });
  if (valid.length === 0) {
    return { eligible: false, reason: 'no_actionable_items' };
  }

  const playbookBacked = valid.filter(isPlaybookRecommendationAction);
  if (playbookBacked.length > 0) {
    return { eligible: true, count: playbookBacked.length, source: 'playbook' };
  }

  if (!hasMeaningfulRetentionRisk(ctx)) {
    return { eligible: false, reason: 'low_risk' };
  }
  if (!hasSufficientConfidence(ctx)) {
    return { eligible: false, reason: 'low_confidence' };
  }
  return { eligible: true, count: valid.length };
}

function qualifiesForRiskDriversBrief(ctx, drivers = []) {
  if (!hasMeaningfulRetentionRisk(ctx)) {
    return { eligible: false, reason: 'low_risk' };
  }
  const valid = (drivers || []).filter((d) => String(d?.text || '').trim().length >= 12);
  if (valid.length === 0) {
    return { eligible: false, reason: 'no_evidence' };
  }
  return { eligible: true, count: valid.length };
}

function getPrimaryRiskStatusMessage(ctx) {
  if (qualifiesForRiskDriverSection(ctx).eligible) return null;
  if (isLowRetentionRisk(ctx)) return POSITIVE_STATUS.NO_RETENTION_CONCERNS;
  if (!ctx.scoreReady) return null;
  return null;
}

function getListReasonDisplay(ctx) {
  if (qualifiesForRiskDriverSection(ctx).eligible) {
    return { mode: 'driver', statusMessage: null };
  }
  if (isLowRetentionRisk(ctx)) {
    return { mode: 'positive', statusMessage: POSITIVE_STATUS.NO_RETENTION_CONCERNS };
  }
  if (!ctx.scoreReady) {
    return { mode: 'pending', statusMessage: null };
  }
  if (ctx.turnover || ctx.employee) {
    const { resolveProgressiveInsight } = require('./progressiveInsight');
    return {
      mode: 'driver',
      statusMessage: resolveProgressiveInsight({
        employee: ctx.employee,
        turnover: ctx.turnover,
        purpose: 'top_risk_driver',
      }),
    };
  }
  return { mode: 'none', statusMessage: null };
}

/**
 * Central eligibility evaluation for an employee surface.
 * @param {{ turnover?: object|null, employee?: object|null, compBench?: object|null, playbook?: object|null, topRiskDrivers?: object[], protectiveFactors?: object[], actions?: object[] }} input
 */
function evaluateContentEligibility(input = {}) {
  const ctx = buildRiskContext(input.turnover, {
    employee: input.employee,
    compBench: input.compBench,
  });
  const actions = input.actions ?? input.playbook?.actions ?? [];

  const primaryRiskDriver = qualifiesForPrimaryRiskDriver(ctx);
  const secondaryRiskDrivers = qualifiesForSecondaryRiskDrivers(ctx);
  const topRiskDriversBrief = qualifiesForRiskDriversBrief(ctx, input.topRiskDrivers);
  const protectiveFactors = qualifiesForProtectiveFactors(ctx, input.protectiveFactors);
  const recommendations = qualifiesForRecommendations(ctx, actions);
  const compensationInsight = qualifiesForCompensationInsight(ctx);

  return {
    context: ctx,
    sections: {
      primaryRiskDriver,
      secondaryRiskDrivers,
      topRiskDriversBrief,
      protectiveFactors,
      recommendations,
      compensationInsight,
    },
    display: {
      primaryRiskStatusMessage: getPrimaryRiskStatusMessage(ctx),
      listReason: getListReasonDisplay(ctx),
      showPositiveOutlook: isLowRetentionRisk(ctx),
    },
    thresholds: {
      LOW_RISK_SCORE_MAX,
      MEANINGFUL_RISK_SCORE_MIN,
    },
  };
}

module.exports = {
  POSITIVE_STATUS,
  LOW_RISK_SCORE_MAX,
  MEANINGFUL_RISK_SCORE_MIN,
  buildRiskContext,
  hasMeaningfulRetentionRisk,
  isLowRetentionRisk,
  hasSufficientConfidence,
  driverHasEvidence,
  qualifiesForRiskDriverSection,
  qualifiesForPrimaryRiskDriver,
  qualifiesForSecondaryRiskDrivers,
  qualifiesForProtectiveFactors,
  qualifiesForCompensationInsight,
  qualifiesForRecommendations,
  qualifiesForRiskDriversBrief,
  getPrimaryRiskStatusMessage,
  getListReasonDisplay,
  evaluateContentEligibility,
  compGapPctFromContext,
};
